import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { ICONS, PageIcon } from "@/lib/icons";
import { PageHeader, Panel, Empty } from "@/components/common";

const EMPTY = { title: "", icon: "FileText", description: "", content: "", order: 0, show_subscriptions: true, show_documents: true };

function PageDialog({ page, onClose, onSaved }) {
  const [f, setF] = useState(page);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, order: Number(f.order) || 0 };
      if (f.id) await api.put(`/admin/pages/${f.id}`, body);
      else await api.post("/admin/pages", body);
      toast.success("Pagina salvata");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-[#111827] border-slate-800">
        <DialogHeader><DialogTitle>{f.id ? "Modifica pagina" : "Nuova pagina"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-[1fr_140px_90px] gap-3">
            <div className="space-y-1.5"><Label>Titolo *</Label><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="page-title-input" /></div>
            <div className="space-y-1.5">
              <Label>Icona</Label>
              <Select value={f.icon} onValueChange={(v) => setF({ ...f, icon: v })}>
                <SelectTrigger data-testid="page-icon-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(ICONS).map((k) => (
                    <SelectItem key={k} value={k}><span className="flex items-center gap-2"><PageIcon name={k} size={14} />{k}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Ordine</Label><Input type="number" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} data-testid="page-order-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Descrizione breve</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="page-description-input" /></div>
          <div className="space-y-1.5"><Label>Contenuto libero (uguale per tutti i clienti)</Label><Textarea rows={5} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="page-content-input" /></div>
          <div className="flex gap-8">
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.show_subscriptions} onCheckedChange={(v) => setF({ ...f, show_subscriptions: v })} data-testid="page-show-subs-switch" />Mostra abbonamenti</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.show_documents} onCheckedChange={(v) => setF({ ...f, show_documents: v })} data-testid="page-show-docs-switch" />Mostra documenti</label>
          </div>
          <DialogFooter><Button type="submit" className="bg-blue-600 hover:bg-blue-500" data-testid="page-save-button">Salva</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/pages").then((r) => setPages(r.data));
  useEffect(() => {
    load();
  }, []);
  const remove = async (p) => {
    if (!window.confirm(`Eliminare la pagina "${p.title}"? Verranno rimossi anche abbonamenti e documenti collegati.`)) return;
    await api.delete(`/admin/pages/${p.id}`);
    toast.success("Pagina eliminata");
    load();
  };
  return (
    <div>
      <PageHeader eyebrow="Consolle amministrazione" title="Pagine" desc="Le pagine sono uguali per tutti i clienti: ognuno vedrà solo i propri abbonamenti e documenti."
        actions={<Button onClick={() => setEditing({ ...EMPTY, order: pages.length + 1 })} className="bg-blue-600 hover:bg-blue-500" data-testid="add-page-button"><Plus size={16} className="mr-1" />Nuova pagina</Button>} />
      <Panel testid="pages-list">
        {pages.length ? (
          <ul className="divide-y divide-slate-800">
            {pages.map((p) => (
              <li key={p.id} className="flex items-center gap-4 px-5 py-4" data-testid={`page-row-${p.slug}`}>
                <span className="w-6 text-xs font-mono text-slate-600">{p.order}</span>
                <div className="h-10 w-10 rounded-md bg-blue-500/10 border border-blue-500/20 grid place-items-center text-blue-400"><PageIcon name={p.icon} size={18} /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{p.title}</div>
                  <div className="text-xs text-slate-500 truncate">{p.description || "—"}</div>
                </div>
                <div className="hidden sm:flex gap-2 text-[11px]">
                  {p.show_subscriptions && <span className="rounded border border-slate-700 px-2 py-0.5 text-slate-400">Abbonamenti</span>}
                  {p.show_documents && <span className="rounded border border-slate-700 px-2 py-0.5 text-slate-400">Documenti</span>}
                </div>
                <Button size="icon" variant="ghost" onClick={() => setEditing(p)} data-testid={`edit-page-${p.slug}`}><Pencil size={15} /></Button>
                <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => remove(p)} data-testid={`delete-page-${p.slug}`}><Trash2 size={15} /></Button>
              </li>
            ))}
          </ul>
        ) : <Empty text="Nessuna pagina creata." />}
      </Panel>
      {editing && <PageDialog page={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
